import { Theme } from '@eversports/react-components/design-tokens/theme'
import Button from '@eversports/react-components/primitives/Button'
import Checkbox from '@eversports/react-components/primitives/Checkbox'
import ErrorBox from '@eversports/react-components/primitives/ErrorBox'
import { SimpleFlex, VerticalFlex } from '@eversports/react-components/primitives/Flex'
import Text from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'
import { usePyroField } from 'pyro-form'
import React, { useEffect, useState } from 'react'
import { CardElement } from '@stripe/react-stripe-js'
import { StripeCardElement, StripeCardElementChangeEvent } from '@stripe/stripe-js'
import useCheckoutContext from '../../data/useCheckoutContext'
import { Localized } from '../../localization/react'
import { NEW_CARD, PaymentMethodType } from '../../typings/Payment'

const useStyles = makeStyles(({ palette, borders, layoutSpacing, borderRadii }: Theme) => ({
  container: {
    cursor: 'pointer',
  },
  icons: {
    border: borders.default,
    borderColor: palette.divider,
    padding: `0 ${layoutSpacing.default}`,
    height: 36,
  },
  cardElement: ({ isSelected }: { isSelected: boolean }) => ({
    border: borders.strong,
    borderColor: isSelected ? palette.primary.main : palette.divider,
    borderRadius: borderRadii.default,
    padding: layoutSpacing.default,
    cursor: 'pointer',
  }),
  fullWidth: {
    width: '100%',
  },
  newCard: {
    border: borders.default,
    borderRadius: borderRadii.default,
    alignSelf: 'flex-start',
  },
}))

type CardValidationErrorMessage = string | undefined

interface Props {
  hasStoredCreditCards: boolean
}

const NewCreditCard = ({ hasStoredCreditCards }: Props) => {
  const context = useCheckoutContext()
  const { core: cardIdPyroField } = usePyroField('cardId')
  const { core: typePyroField } = usePyroField('type')
  const { core: saveCardPyroField } = usePyroField('saveCard')
  const { core: selectedPaymentMethod } = usePyroField('type')

  const [isSelected, setIsSelected] = useState(true)
  const [hideSaveCard, setHideSaveCard] = useState(false)
  const [showCardElement, setShowCardElement] = useState(
    selectedPaymentMethod.value === PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD && !hasStoredCreditCards,
  )
  const [cardValidationErrorMessage, setCardValidationErrorMessage] = useState<CardValidationErrorMessage>(undefined)

  const classes = useStyles({ isSelected })

  const handleShowCardElementClick = () => {
    setShowCardElement(true)
  }

  // if we pre-selected the save card option on the payment form, its because
  // we don't want to allow the user to interact with this option
  // ex: when buying a membership
  useEffect(() => {
    if (saveCardPyroField.value.length === 0) return
    setHideSaveCard(true)
  }, [])

  // I'm currently managing manually the pyro-form state for the new card component
  // because didn't find a way to mix well the radio button and the stripe card element
  // in order to leverage the state management automatically
  useEffect(() => {
    if (!typePyroField.value || !cardIdPyroField.value) return

    // When we press another payment method or saved card
    if (typePyroField.value !== PaymentMethodType.PAYMENT_METHOD_CREDIT_CARD || cardIdPyroField.value !== NEW_CARD) {
      setShowCardElement(false)
    } else {
      setShowCardElement(true)
    }
  }, [cardIdPyroField.value, typePyroField.value])

  useEffect(() => {
    if (!showCardElement) return

    context.setIsPaymentValid(false)
    cardIdPyroField.onChange(NEW_CARD)
    setCardValidationErrorMessage(undefined)
    setIsSelected(true)
  }, [showCardElement])

  const handleCardInputChange = (event: StripeCardElementChangeEvent) => {
    const errorMessage = event.error ? event.error.message : undefined

    setCardValidationErrorMessage(errorMessage)

    // sync context every time the input changes
    context.setIsPaymentValid(event.complete)
  }

  const handleOnReady = (stripeElement: StripeCardElement) => {
    stripeElement.focus()
  }

  return (
    <VerticalFlex verticalSpacing="default" className={classes.fullWidth}>
      {!showCardElement && (
        <Button
          variant="secondary"
          onClick={handleShowCardElementClick}
          className={classes.newCard}
          data-testid="show-credit-card-input"
        >
          <Text size="small" fontWeight="bold" color="primary.main">
            <Localized id="payment-pay-with-credit-card-add-new-credit-card" />
          </Text>
        </Button>
      )}
      {showCardElement && (
        <>
          <label className={classes.cardElement}>
            <CardElement onReady={handleOnReady} onChange={handleCardInputChange} id="credit-card-input" />
          </label>
          {cardValidationErrorMessage && <ErrorBox error={{ type: 'message', message: cardValidationErrorMessage }} />}
          {!hideSaveCard && (
            <SimpleFlex alignItems="center" as="label">
              <Checkbox name="saveCard" value={String(true)} disabled={!isSelected} />
              <Text size="small">
                <Localized id="save-card" />
              </Text>
            </SimpleFlex>
          )}
        </>
      )}
    </VerticalFlex>
  )
}

export default NewCreditCard
